import {
	useState,
	useEffect,
	useCallback,
	useMemo,
	useEffectEvent,
} from "react";
import { TableColumn } from "@/components/table/data-table";

interface ColumnVisibilityState {
	visibleColumns: string[];
	toggleColumn: (columnKey: string) => void;
	setColumnVisible: (columnKey: string, visible: boolean) => void;
	resetToDefaults: () => void;
	isColumnVisible: (columnKey: string) => boolean;
	getVisibleColumns: () => TableColumn[];
	allColumns: TableColumn[];
}

export function useColumnVisibility(
	tableId: string,
	columns: TableColumn[],
): ColumnVisibilityState {
	// ✅ OPTIMIZACIÓN 1: Memoizar storageKey
	const storageKey = useMemo(() => `table-columns-${tableId}`, [tableId]);

	// ✅ OPTIMIZACIÓN 2: Memoizar requiredColumns
	const requiredColumns = useMemo(
		() =>
			new Set(
				columns
					.filter((col) => col.required === true)
					.map((col) => col.key),
			),
		[columns],
	);

	// ✅ OPTIMIZACIÓN 3: Memoizar allColumnKeys
	const allColumnKeys = useMemo(
		() => columns.map((col) => col.key),
		[columns],
	);

	// ✅ OPTIMIZACIÓN 4: Función helper pura (no useEffectEvent)
	const ensureMinimumColumns = useCallback(
		(visibleSet: Set<string>) => {
			// Asegurar columnas obligatorias
			requiredColumns.forEach((colKey) => visibleSet.add(colKey));

			// Asegurar al menos una columna visible
			if (visibleSet.size === 0) {
				const firstAvailable =
					columns.find((col) => !requiredColumns.has(col.key)) ||
					columns[0];
				if (firstAvailable) {
					visibleSet.add(firstAvailable.key);
				}
			}
		},
		[requiredColumns, columns],
	);

	// ✅ OPTIMIZACIÓN 5: useEffectEvent solo para localStorage (side effect)
	const persistVisibility = useEffectEvent((newVisibleColumns: string[]) => {
		try {
			localStorage.setItem(storageKey, JSON.stringify(newVisibleColumns));
		} catch (error) {
			console.warn("Error saving column visibility to localStorage:", error);
		}
	});

	// Estado inicial
	const getInitialVisibleColumns = useCallback((): string[] => {
		try {
			const stored = localStorage.getItem(storageKey);
			if (stored) {
				const parsedVisible = JSON.parse(stored) as string[];
				const visibleSet = new Set(parsedVisible);

				// Asegurar columnas obligatorias
				requiredColumns.forEach((colKey) => visibleSet.add(colKey));

				if (visibleSet.size === 0) {
					return allColumnKeys;
				}

				return Array.from(visibleSet);
			}
		} catch (error) {
			console.warn(
				"Error loading column visibility from localStorage:",
				error,
			);
		}

		return allColumnKeys;
	}, [storageKey, allColumnKeys, requiredColumns]);

	const [visibleColumns, setVisibleColumns] = useState<string[]>(
		getInitialVisibleColumns,
	);

	// Actualizar cuando cambien las columnas
	useEffect(() => {
		setVisibleColumns(getInitialVisibleColumns());
	}, [getInitialVisibleColumns]);

	// ✅ OPTIMIZACIÓN 6: Toggle optimizado
	const toggleColumn = useCallback(
		(columnKey: string) => {
			setVisibleColumns((prev) => {
				// No permitir ocultar columnas obligatorias
				if (requiredColumns.has(columnKey)) {
					console.log("⚠️ Cannot hide required column:", columnKey);
					return prev;
				}

				const visibleSet = new Set(prev);

				if (visibleSet.has(columnKey)) {
					visibleSet.delete(columnKey);
					console.log("👁️ Hiding column:", columnKey);
				} else {
					visibleSet.add(columnKey);
					console.log("👁️ Showing column:", columnKey);
				}

				// ✅ Llamar función helper pura (no useEffectEvent)
				ensureMinimumColumns(visibleSet);

				const newVisible = Array.from(visibleSet);

				// ✅ persistVisibility se ejecutará después del render
				return newVisible;
			});
		},
		[requiredColumns, ensureMinimumColumns],
	);

	// ✅ OPTIMIZACIÓN 7: Efecto para persistir después del cambio
	useEffect(() => {
		persistVisibility(visibleColumns);
	}, [visibleColumns, persistVisibility]);

	// ✅ OPTIMIZACIÓN 8: setColumnVisible optimizado
	const setColumnVisible = useCallback(
		(columnKey: string, visible: boolean) => {
			setVisibleColumns((prev) => {
				// No permitir ocultar columnas obligatorias
				if (!visible && requiredColumns.has(columnKey)) {
					return prev;
				}

				const visibleSet = new Set(prev);

				if (visible) {
					visibleSet.add(columnKey);
				} else {
					visibleSet.delete(columnKey);
				}

				ensureMinimumColumns(visibleSet);

				return Array.from(visibleSet);
			});
		},
		[requiredColumns, ensureMinimumColumns],
	);

	// ✅ OPTIMIZACIÓN 9: resetToDefaults optimizado
	const resetToDefaults = useCallback(() => {
		setVisibleColumns(allColumnKeys);
	}, [allColumnKeys]);

	// ✅ OPTIMIZACIÓN 10: isColumnVisible con Set para O(1)
	const visibleColumnsSet = useMemo(
		() => new Set(visibleColumns),
		[visibleColumns],
	);

	const isColumnVisible = useCallback(
		(columnKey: string): boolean => {
			return visibleColumnsSet.has(columnKey);
		},
		[visibleColumnsSet],
	);

	// ✅ OPTIMIZACIÓN 11: getVisibleColumns memoizado
	const getVisibleColumns = useCallback((): TableColumn[] => {
		return columns.filter((col) => visibleColumnsSet.has(col.key));
	}, [columns, visibleColumnsSet]);

	return {
		visibleColumns,
		toggleColumn,
		setColumnVisible,
		resetToDefaults,
		isColumnVisible,
		getVisibleColumns,
		allColumns: columns,
	};
}

export default useColumnVisibility;
